/** One lever the review card can assign. `name` is the value stored on commit. */
export type InitiativeLeverOption = {
    group: string;
    title: string;
    description: string;
    name: string;
};

/**
 * Uncommitted initiative draft from `propose_initiative`.
 * It lives on the chat message until the user commits.
 */
export type InitiativeProposal = {
    title: string;
    description: string;
    /** Lever `name` (`Group — Title`). */
    lever: string;
    priority: string;
    /** ISO date `YYYY-MM-DD`. */
    start_date: string;
    levers: InitiativeLeverOption[];
    priorities: string[];
};

function parseLeverOption(value: unknown): InitiativeLeverOption | undefined {
    if (value == null || typeof value !== "object") return undefined;
    const o = value as Record<string, unknown>;
    if (typeof o.name !== "string" || o.name === "") return undefined;
    return {
        group: typeof o.group === "string" ? o.group : "",
        title: typeof o.title === "string" ? o.title : "",
        description: typeof o.description === "string" ? o.description : "",
        name: o.name,
    };
}

/** Parses a propose_initiative tool result. Anything else leaves the card hidden. */
export function parseInitiativeProposal(response: unknown): InitiativeProposal | undefined {
    if (response == null || typeof response !== "object") return undefined;
    const root = response as Record<string, unknown>;
    const proposal = root.proposal;
    if (proposal == null || typeof proposal !== "object") return undefined;
    const fields = proposal as Record<string, unknown>;
    const title = fields.title;
    const description = fields.description;
    const lever = fields.lever;
    const priority = fields.priority;
    const startDate = fields.start_date;
    if (typeof title !== "string" || typeof description !== "string") return undefined;
    if (typeof lever !== "string" || typeof priority !== "string" || typeof startDate !== "string") {
        return undefined;
    }
    if (!Array.isArray(root.levers) || !Array.isArray(root.priorities)) return undefined;
    const levers = root.levers.map(parseLeverOption).filter((item): item is InitiativeLeverOption => item != null);
    const priorities = root.priorities.filter((item): item is string => typeof item === "string" && item !== "");
    if (levers.length === 0 || priorities.length === 0) return undefined;
    return {
        title,
        description,
        lever,
        priority,
        start_date: startDate,
        levers,
        priorities,
    };
}
